import { useCallback, useEffect, useState } from 'react'
import {
  Bidi, Button, Card, CardHeader, Field, FieldInput, LoadingState, NumberField, Switch,
} from '@hk/ui'
import {
  addRequestCategory, codeWellFormed, deleteRequestCategory, fetchRequestCategories,
  formatCount, saveRequestCategory, setRequestCategoryActive, suggestCategoryCode,
  DEFAULT_PROMISE_MINUTES, MAX_PROMISE_MINUTES, MIN_PROMISE_MINUTES,
  type RequestCategoryRow,
} from '@hk/core'
import type { AppLocale } from '@hk/ui'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-129, SECOND CARD · WHAT A GUEST MAY ASK FOR.  Sprint 56.

   ── WHY THIS EXISTS ──────────────────────────────────────────────────────────────────
   Six kinds of guest request have been in this product since Sprint 1 because a developer
   wrote them into a seed file. They are a reasonable guess at what a hotel in Najaf is
   asked for; they are not the hotel's list. The same reasoning as CQ-130 one sprint on —
   **a word this product invented that the hotel cannot correct is a defect with a delay on
   it** — except that these words are load-bearing: a category's code is what every report
   counts by and what every request ever raised points at.

   ── WHAT THE SCREEN REFUSES TO OFFER ─────────────────────────────────────────────────
   There is no box anywhere on this card for changing a code. Not a disabled one, not a
   greyed one — the field does not exist, because a control somebody can see is a control
   somebody will ask about, and the answer would have to be "no, never, for a reason about
   reports". The code is drawn as text, in the row's identity column, beside everything
   that can change.

   The REMOVE button is drawn only on a row nothing has ever been raised against, and the
   cell says why in either case. A button that is visible and then refuses has already
   wasted the person's decision, and #152's lesson applies to hints as much as to labels:
   the sentence has to belong to the row it is in.
   ========================================================================= */

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole)
}

function pluralOf(forms: Record<string, string>, count: number, locale: AppLocale): string {
  return forms[new Intl.PluralRules(locale).select(count)] ?? forms.other ?? ''
}

interface Draft {
  name: string
  nameAr: string
  promiseMinutes: number | null
  isCountable: boolean
}

const draftOf = (row: RequestCategoryRow): Draft => ({
  name: row.name,
  nameAr: row.nameAr ?? '',
  promiseMinutes: row.promiseMinutes,
  isCountable: row.isCountable,
})

const changed = (row: RequestCategoryRow, draft: Draft) =>
  draft.name !== row.name
  || draft.nameAr !== (row.nameAr ?? '')
  || draft.promiseMinutes !== row.promiseMinutes
  || draft.isCountable !== row.isCountable

export function RequestCategories({
  propertyId,
  locale,
  onError,
}: {
  propertyId: string
  locale: AppLocale
  /** Raised to the screen, which owns the one alert band. Two error bands on one page is
   *  two places to look for the same kind of news. */
  onError: (caught: unknown) => void
}) {
  const t = STRINGS[locale]

  const [rows, setRows] = useState<RequestCategoryRow[] | null>(null)
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [busy, setBusy] = useState<string | null>(null)

  /* The new row, while it is being typed. Null when the form is closed — a form that is
     always open is a form somebody submits by accident. */
  const [adding, setAdding] = useState<{
    code: string
    /** Whether the code box has been typed in. Until it has, the name drives it. */
    codeTouched: boolean
    name: string
    nameAr: string
    promiseMinutes: number | null
    isCountable: boolean
  } | null>(null)

  const take = useCallback((next: RequestCategoryRow[]) => {
    setRows(next)
    setDrafts(Object.fromEntries(next.map((r) => [r.categoryId, draftOf(r)])))
  }, [])

  const load = useCallback(async () => {
    try {
      take(await fetchRequestCategories(propertyId))
    } catch (caught: unknown) {
      onError(caught)
    }
  }, [propertyId, take, onError])

  useEffect(() => { void load() }, [load])

  if (rows === null) return <LoadingState rows={4} />

  async function commit(what: string, run: () => Promise<RequestCategoryRow[]>) {
    setBusy(what)
    try {
      take(await run())
      return true
    } catch (caught: unknown) {
      onError(caught)
      return false
    } finally {
      setBusy(null)
    }
  }

  const codeToUse = adding === null
    ? ''
    : adding.codeTouched ? adding.code.trim().toUpperCase() : suggestCategoryCode(adding.name)

  const addable =
    adding !== null
    && adding.name.trim().length > 0
    && codeWellFormed(codeToUse)
    && adding.promiseMinutes !== null
    && adding.promiseMinutes >= MIN_PROMISE_MINUTES
    && adding.promiseMinutes <= MAX_PROMISE_MINUTES

  /* **HOW MANY ARE STILL ON**, computed here so the last one's switch can be disabled
     rather than refused. The server checks it too, and the server is the one that decides —
     this only stops the screen offering a control that cannot work. */
  const stillActive = rows.filter((r) => r.isActive).length

  return (
    <Card>
      <CardHeader title={t.rcTitle} />
      <p className="max-w-[70ch] px-3.5 pt-0 text-[12.5px] leading-relaxed text-muted-foreground">
        {t.rcIntro}
      </p>

      <div className="overflow-x-auto p-3.5">
        {/* **THE NAME COLUMNS GET ROOM, AND THE REST GET WHAT IS LEFT** — #157.

            The first draft let eight columns share the width evenly, and the boxes holding
            the thing somebody is editing came out narrower than the words in them:
            "Turndown ser", "Change of lin", "Something el". A person cannot correct a name
            they cannot read. Found by looking at the rendered English screen — the Arabic
            one was fine, because Arabic is shorter here, which is exactly why looking at
            only one language finds only half of these. */}
        <table className="w-full min-w-[63rem] table-fixed text-[12.5px]">
          {/* **THE MINIMUM WIDTH IS THE SUM OF THE COLUMNS, NOT A ROUND NUMBER.** The
              first attempt at this fix set it lower than the columns add up to, so a
              narrow window squeezed them all and the buttons were clipped at the edge
              instead — "Switch", "Remov". With `table-fixed` a minimum that does not
              cover the columns is a minimum that does nothing. */}
          <colgroup>
            <col className="w-[7.5rem]" />
            <col className="w-[12rem]" />
            <col className="w-[12rem]" />
            <col className="w-[6.5rem]" />
            <col className="w-[4.5rem]" />
            <col className="w-[6.5rem]" />
            <col className="w-[5.5rem]" />
            <col className="w-[8.5rem]" />
          </colgroup>
          <thead>
            <tr className="border-b border-border text-muted-foreground">
              <th className="p-2 text-start font-medium">{t.rcColCode}</th>
              <th className="p-2 text-start font-medium">{t.rcColName}</th>
              <th className="p-2 text-start font-medium">{t.rcColNameAr}</th>
              <th className="p-2 text-start font-medium">{t.rcColPromise}</th>
              <th className="p-2 text-start font-medium">{t.rcColCountable}</th>
              <th className="p-2 text-start font-medium">{t.rcColStock}</th>
              <th className="p-2 text-start font-medium">{t.rcColAsked}</th>
              <th className="p-2 text-start font-medium sr-only">{t.cfgSave}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const draft = drafts[row.categoryId] ?? draftOf(row)
              const stock = locale === 'ar'
                ? row.stockItemNameAr ?? row.stockItemName
                : row.stockItemName

              return (
                <tr
                  key={row.categoryId}
                  className={`border-b border-border/60 last:border-0 ${
                    row.isActive ? '' : 'opacity-60'}`}
                >
                  {/* **THE CODE IS TEXT, NOT A BOX.** There is no control here to disable,
                      because a disabled control is a question somebody has to ask. */}
                  <td className="p-2 font-mono text-[11.5px] text-muted-foreground">
                    <div>{row.code}</div>
                    {row.isActive ? null : (
                      <div className="mt-0.5 font-sans text-[11px] text-muted-foreground">
                        <Bidi>{t.rcRetired}</Bidi>
                      </div>
                    )}
                  </td>

                  <td className="p-2">
                    <FieldInput
                      aria-label={`${t.rcColName} — ${row.code}`}
                      dir="auto"
                      maxLength={80}
                      value={draft.name}
                      onChange={(e) => setDrafts((d) => ({
                        ...d, [row.categoryId]: { ...draft, name: e.target.value },
                      }))}
                    />
                  </td>

                  <td className="p-2">
                    <FieldInput
                      aria-label={`${t.rcColNameAr} — ${row.code}`}
                      dir="rtl"
                      lang="ar"
                      maxLength={80}
                      value={draft.nameAr}
                      onChange={(e) => setDrafts((d) => ({
                        ...d, [row.categoryId]: { ...draft, nameAr: e.target.value },
                      }))}
                    />
                  </td>

                  <td className="p-2">
                    {/* **`NumberField`, NOT A BARE INPUT** — #154's lesson, one sprint on.
                        A bare number box draws 30 and 45 in Western digits in a column of
                        Arabic text, which is the defect that has been found four times in
                        this product and only ever by looking at the rendered screen. */}
                    <NumberField
                      aria-label={`${t.rcColPromise} — ${row.code}`}
                      className="w-20"
                      min={MIN_PROMISE_MINUTES}
                      max={MAX_PROMISE_MINUTES}
                      value={draft.promiseMinutes}
                      onChange={(next) => setDrafts((d) => ({
                        ...d, [row.categoryId]: { ...draft, promiseMinutes: next },
                      }))}
                    />
                  </td>

                  <td className="p-2">
                    <Switch
                      aria-label={`${t.rcCountableLabel} — ${row.code}`}
                      checked={draft.isCountable}
                      onCheckedChange={(next) => setDrafts((d) => ({
                        ...d, [row.categoryId]: { ...draft, isCountable: next },
                      }))}
                    />
                  </td>

                  <td className="p-2 text-muted-foreground">
                    {/* **WHAT DELIVERING IT TAKES OFF A SHELF**, by name. Read-only here:
                        choosing one needs the stock list, and a picker that loads a second
                        screen's data to fill one cell is how this card would become slow
                        for everybody to serve a column most rows leave empty. */}
                    <Bidi>{stock ?? t.rcNoStock}</Bidi>
                  </td>

                  <td className="p-2 text-muted-foreground">
                    <Bidi>
                      {row.requestsEverRaised === 0
                        ? t.rcNeverAsked
                        : fill(pluralOf(t.rcAsked, row.requestsEverRaised, locale),
                            { count: formatCount(row.requestsEverRaised, locale) })}
                    </Bidi>
                  </td>

                  <td className="p-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!changed(row, draft)
                          || draft.promiseMinutes === null
                          || busy === row.categoryId}
                        aria-busy={busy === row.categoryId}
                        aria-label={`${t.cfgSave} — ${row.code}`}
                        onClick={() => void commit(row.categoryId, () =>
                          saveRequestCategory(propertyId, row.categoryId, {
                            name: draft.name,
                            nameAr: draft.nameAr,
                            promiseMinutes: draft.promiseMinutes ?? row.promiseMinutes,
                            isCountable: draft.isCountable,
                            stockItemId: row.stockItemId,
                          }))}
                      >
                        {busy === row.categoryId ? t.cfgSaving : t.cfgSave}
                      </Button>

                      {/* **THE LAST ACTIVE ONE'S SWITCH IS DISABLED, NOT REFUSED.** An
                          empty picker leaves the front desk and every QR code in the
                          building with nothing to choose, and nothing else in the product
                          would say so. */}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy === row.categoryId
                          || (row.isActive && stillActive <= 1)}
                        aria-label={
                          `${row.isActive ? t.rcRetire : t.rcRestore} — ${row.code}`}
                        onClick={() => void commit(row.categoryId, () =>
                          setRequestCategoryActive(propertyId, row.categoryId, !row.isActive))}
                      >
                        {row.isActive ? t.rcRetire : t.rcRestore}
                      </Button>

                      {/* **DRAWN ONLY WHERE IT CAN WORK.** */}
                      {row.requestsEverRaised === 0 ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={busy === row.categoryId}
                          aria-label={`${t.rcDelete} — ${row.code}`}
                          onClick={() => void commit(row.categoryId, () =>
                            deleteRequestCategory(propertyId, row.categoryId))}
                        >
                          {t.rcDelete}
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* **SAID ONCE, NOT ONCE PER ROW** — #159. The first draft put this sentence in
            every row's cell, which is the same text six times down one column: noise that
            the eye stops reading, and the thing it explains — why some rows have a Remove
            button and others do not — is a property of the TABLE rather than of a row. */}
        <p className="mt-3 max-w-[70ch] text-[11.5px] leading-relaxed text-muted-foreground">
          {t.rcKeepHint}
        </p>
      </div>

      {/* ── ADDING ONE ───────────────────────────────────────────────────── */}
      <div className="border-t border-border p-3.5">
        {adding === null ? (
          <Button
            type="button"
            size="sm"
            onClick={() => setAdding({
              code: '', codeTouched: false, name: '', nameAr: '',
              promiseMinutes: DEFAULT_PROMISE_MINUTES, isCountable: true,
            })}
          >
            {t.rcAdd}
          </Button>
        ) : (
          <div className="flex flex-col gap-3">
            <div>
              <h3 className="text-[13px] font-medium">{t.rcAddTitle}</h3>
              <p className="mt-1 max-w-[70ch] text-[12px] leading-relaxed text-muted-foreground">
                {t.rcAddIntro}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t.rcColName}>
                {({ id, 'aria-label': label }) => (
                  <FieldInput
                    id={id} aria-label={label} dir="auto" maxLength={80}
                    value={adding.name}
                    onChange={(e) => setAdding((a) =>
                      a === null ? a : { ...a, name: e.target.value })}
                  />
                )}
              </Field>

              <Field label={t.rcColNameAr} hint={t.cfgNameArHint}>
                {({ id, 'aria-label': label }) => (
                  <FieldInput
                    id={id} aria-label={label} dir="rtl" lang="ar" maxLength={80}
                    value={adding.nameAr}
                    onChange={(e) => setAdding((a) =>
                      a === null ? a : { ...a, nameAr: e.target.value })}
                  />
                )}
              </Field>

              {/* **THE SUGGESTION IS SHOWN, NEVER APPLIED SILENTLY.** Being helpful behind
                  somebody's back in the one field that is permanent is the worst possible
                  place to do it — so the box fills in from the name until the moment
                  somebody types in it, and then it is theirs. */}
              <Field
                label={t.rcCode}
                hint={adding.codeTouched ? t.rcCodeHint : t.rcCodeSuggested}
              >
                {({ id, 'aria-label': label }) => (
                  <FieldInput
                    id={id}
                    aria-label={label}
                    dir="ltr"
                    lang="en"
                    maxLength={32}
                    className="font-mono"
                    value={codeToUse}
                    onChange={(e) => setAdding((a) => a === null ? a : {
                      ...a, codeTouched: true, code: e.target.value.toUpperCase(),
                    })}
                  />
                )}
              </Field>

              <Field label={t.rcPromise} hint={t.rcPromiseHint}>
                {({ id, 'aria-label': label }) => (
                  <NumberField
                    id={id}
                    aria-label={label}
                    className="w-24"
                    min={MIN_PROMISE_MINUTES}
                    max={MAX_PROMISE_MINUTES}
                    value={adding.promiseMinutes}
                    onChange={(next) => setAdding((a) =>
                      a === null ? a : { ...a, promiseMinutes: next })}
                  />
                )}
              </Field>
            </div>

            <label className="flex items-center gap-2 text-[12.5px]">
              <Switch
                aria-label={t.rcCountableLabel}
                checked={adding.isCountable}
                onCheckedChange={(next) => setAdding((a) =>
                  a === null ? a : { ...a, isCountable: next })}
              />
              <Bidi>{t.rcCountableLabel}</Bidi>
            </label>

            {/* The shape rule, said before anything is sent. The server decides; this stops
                somebody pressing a button to be told what the screen already knew. */}
            {adding.codeTouched && codeToUse.length > 0 && !codeWellFormed(codeToUse) ? (
              <p role="alert" className="text-[12px] text-[#b91c1c] dark:text-[#fca5a5]">
                {t.rcCodeBad}
              </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!addable || busy === 'add'}
                aria-busy={busy === 'add'}
                onClick={() => void (async () => {
                  const ok = await commit('add', () => addRequestCategory(propertyId, {
                    code: codeToUse,
                    name: adding.name,
                    nameAr: adding.nameAr,
                    promiseMinutes: adding.promiseMinutes ?? DEFAULT_PROMISE_MINUTES,
                    isCountable: adding.isCountable,
                    stockItemId: null,
                  }))
                  /* **THE FORM STAYS OPEN WHEN THE SERVER REFUSED**, holding what was
                     typed. Clearing it on a refusal makes somebody type the whole row
                     again to find out what was wrong with it. */
                  if (ok) setAdding(null)
                })()}
              >
                {busy === 'add' ? t.cfgSaving : t.rcAddSave}
              </Button>

              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setAdding(null)}
              >
                {t.rcCancel}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}
